import { NS_DataBlockFields } from '@lmt/expressions/src/expressions/helpers/post-process';
import {
  DataBlockVariant,
  DataBlockWithValuesDto,
  QueryFactory,
  UIConfigDto,
} from '@lmt/shared-types';

import { getFromCacheOrRequest } from '@lmt/unicorn-shared/app/api/custom-hooks';
import { isEmpty } from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/common';
import { QueryClient } from '@tanstack/react-query';

export async function getSharedFieldNames(
  queryClient: QueryClient,
  dataBlockId: number,
  dataBlockType?: QueryFactory.DataBlockTypeDto,
): Promise<string[]> {
  if (
    !dataBlockType ||
    ![DataBlockVariant.Case, DataBlockVariant.Visit].includes(
      dataBlockType.variant,
    )
  ) {
    return [];
  }
  const uiConfigs = await getFromCacheOrRequest(
    queryClient,
    QueryFactory.DataBlockTypeQuery.getUIConfigsQueryKey(dataBlockType.id),
    () => QueryFactory.DataBlockTypeClient.getUIConfigs(dataBlockType.id),
  );

  const dataBlockSharedFields = getSharedFieldNamesFromConfig(uiConfigs);

  if (isEmpty(dataBlockSharedFields)) {
    return [];
  }

  var dataBlock = await getFromCacheOrRequest(
    queryClient,
    QueryFactory.DataBlockQuery.getDataBlockQueryKey(dataBlockId),
    () => QueryFactory.DataBlockClient.getDataBlock(dataBlockId),
  );

  const allNotEmptySharedFieldNames = await getAllNotEmptySharedFieldNames(
    queryClient,
    dataBlockType.variant,
    dataBlock,
  );

  return dataBlockSharedFields.filter((x) =>
    allNotEmptySharedFieldNames.includes(x),
  );
}

function getNotEmptyFieldNames(values: any) {
  if (!values) {
    return [];
  }
  return Object.keys(values).filter((name) => !isEmpty(values[name]));
}

function getSharedFieldNamesFromConfig(configs: UIConfigDto) {
  return getSharedFieldsFromConfig(configs.view)
    .concat(getSharedFieldsFromConfig(configs.details))
    .concat(getSharedFieldsFromConfig(configs.statistic));
}

function getSharedFieldsFromConfig(configString?: string | null) {
  if (!configString) {
    return [];
  }
  const config = JSON.parse(configString);
  return Object.values(config)
    .filter((x) => (x as any)['props']['isShared'] === true)
    .map((x) => (x as any)['props']['dataKey']);
}

async function getAllNotEmptySharedFieldNames(
  queryClient: QueryClient,
  variant: DataBlockVariant,
  dataBlock: DataBlockWithValuesDto,
): Promise<string[]> {
  if (variant === DataBlockVariant.Case) {
    const pCase = await getFromCacheOrRequest(
      queryClient,
      QueryFactory.CaseQuery.getCaseQueryKey(dataBlock.caseId!),
      () => QueryFactory.CaseClient.getCase(dataBlock.caseId!),
    );
    return getNotEmptyFieldNames(pCase.values);
  }
  if (variant === DataBlockVariant.Visit) {
    const visit = await getFromCacheOrRequest(
      queryClient,
      QueryFactory.AppointmentEventsQuery.getVisitQueryKey(dataBlock.visitId!),
      () => QueryFactory.AppointmentEventsClient.getVisit(dataBlock.visitId!),
    );
    return getNotEmptyFieldNames(visit.values);
  }
  return [];
}
